import graphql__0e4ea9acbbe3002ad0fb from "./__generated__/AppSettingsFormQuery.graphql.ts";
import graphql__d1dd54402a9c4f82d878 from "./__generated__/AppSettingsFormUpdateMutation.graphql.ts";import {
  Button,
  Input,
  MessagePanel,
  TextArea,
  validations,
} from '@portal/components'
import * as Sentry from '@sentry/browser'
import graphql from 'babel-plugin-relay/macro'
import { useFormik } from 'formik'
import { Suspense, useEffect, useState } from 'react'
import {
  PreloadedQuery,
  useMutation,
  usePreloadedQuery,
  useQueryLoader,
} from 'react-relay/hooks'
import { RRNLRequestError } from 'react-relay-network-modern'
import { useLocation, useNavigate } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import * as Yup from 'yup'

import { AppSettingsFormQuery } from './__generated__/AppSettingsFormQuery.graphql'
import { AppSettingsFormUpdateMutation } from './__generated__/AppSettingsFormUpdateMutation.graphql'

type FormValues = {
  name: string
  slug: string
  description: string
}

const AppFormSchema = Yup.object().shape({
  name: Yup.string()
    .required('This value is required')
    .min(3, 'Must be at least 3 characters long.')
    .max(50, 'Must be less than 50 characters long.'),
  slug: Yup.string()
    .matches(
      validations.slugRegex,
      'Only a-z, 0-9 and dashes in the middle are allowed',
    )
    .required('This value is required')
    .min(3, 'Must be at least 3 characters long.')
    .max(50, 'Must be at less than 50 characters long.'),
  description: Yup.string().default(''),
})

export const query = graphql__0e4ea9acbbe3002ad0fb

const updateMutation = graphql__d1dd54402a9c4f82d878

type Props = {
  queryRef: PreloadedQuery<AppSettingsFormQuery>
}

/** Form used to update an app's settings. */
const AppSettingsForm = ({ queryRef }: Props) => {
  const [updateError, setUpdateError] = useState<string>('')
  const { addToast } = useToasts()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const {
    viewer: { appById: app },
  } = usePreloadedQuery<AppSettingsFormQuery>(query, queryRef)
  const [updateApp, isUpdatingApp] =
    useMutation<AppSettingsFormUpdateMutation>(updateMutation)

  const viewerCanUpdate = app?.viewerCanUpdate ?? false
  const currentAppSlug = app?.slug || ''

  const replaceAppSlugInPathname = (nextSlug: string) => {
    const pathParts = pathname.split('/')

    const slugIndex = pathParts.indexOf(currentAppSlug)

    pathParts.splice(slugIndex, 1, nextSlug)

    return pathParts.join('/')
  }

  const formik = useFormik<FormValues>({
    initialValues: {
      name: app?.name || '',
      description: app?.description || '',
      slug: app?.slug || '',
    },
    validationSchema: AppFormSchema,
    onSubmit: (values) => {
      if (!app || !viewerCanUpdate) return

      setUpdateError('')
      updateApp({
        variables: {
          id: app.id,
          name: values.name,
          description: values.description,
          slug: values.slug,
        },
        onCompleted: ({ updateApp }) => {
          addToast("Successfully updated the app's settings", {
            appearance: 'success',
            autoDismiss: true,
          })
          if (updateApp.slug !== currentAppSlug) {
            navigate(replaceAppSlugInPathname(updateApp.slug), {
              replace: true,
            })
          }
        },
        onError: (err) => {
          if (err instanceof RRNLRequestError) {
            setUpdateError(err.res?.errors?.[0]?.message || err.message)
          } else {
            setUpdateError(err.message)
          }
        },
      })
    },
  })

  /** Report missing data or data to help debug SURF-294 */
  useEffect(() => {
    if (!app) {
      Sentry.captureMessage(`Failed to load app for AppSettingsFormQuery`, {
        level: 'warning',
        tags: {
          app: JSON.stringify(app),
        },
      })
    }
  }, [app])

  if (!app) {
    return null
  }

  return (
    <form
      onSubmit={formik.handleSubmit}
      data-testid="updateAppForm"
      className="max-w-xl space-y-8"
    >
      {!!updateError.length && (
        <MessagePanel variant="danger" message={updateError} className="mb-3" />
      )}
      <div>
        <Input
          disabled={isUpdatingApp || !viewerCanUpdate}
          id="name"
          label="Name"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.name}
          errorMessage={formik.errors.name}
          className="w-full"
          required
        />
      </div>
      <div>
        <Input
          disabled={isUpdatingApp || !viewerCanUpdate}
          label="Slug"
          id="slug"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          value={formik.values.slug}
          errorMessage={formik.errors.slug}
          placeholder="app-slug"
          autoComplete="off"
          addOn={`${window.location.protocol}//${window.location.host}/${
            app.tenant.__typename === 'Organization'
              ? app.tenant.slug + '/'
              : 'app/'
          }`}
          required
        />
      </div>
      <div>
        <TextArea
          disabled={isUpdatingApp || !viewerCanUpdate}
          label="Description"
          id="description"
          rows={5}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.description}
          data-testid="descriptionInput"
          className="w-full"
        />
      </div>
      <div className="self-start">
        <Button
          variant="primary"
          type="submit"
          disabled={isUpdatingApp || !viewerCanUpdate}
        >
          {isUpdatingApp ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  )
}

type ContainerProps = {
  //Current app ID
  appId: string
}

const AppSettingsFormContainer = ({ appId }: ContainerProps) => {
  const [queryRef, loadQuery] = useQueryLoader<AppSettingsFormQuery>(query)

  useEffect(() => {
    if (appId) {
      loadQuery({ appId }, { fetchPolicy: 'store-and-network' })
    }
  }, [appId, loadQuery])

  if (!queryRef) {
    return null
  }

  return (
    <Suspense>
      <AppSettingsForm queryRef={queryRef} />
    </Suspense>
  )
}

export default AppSettingsFormContainer
